import { useMemo } from "react";
import { useDimensions } from "./useDimensions";
import { timeScale } from "components/charts/shared/bsi-time-scale";
import moment from "moment";
import { getTickCountBySize } from "components/charts/shared/helpers";

const dimensionsValid = (dimensions: DOMRect | null) => {
  return dimensions && dimensions.width > 0;
};

const barChartSizing = (width: number): ChartPeriod => {
  if (width < 500) {
    return CHART_PERIOD.MONTH;
  }
  if (width < 1500) {
    return CHART_PERIOD.WEEK;
  }

  return CHART_PERIOD.DAY;
};

const maxChartPeriod = (first: ChartPeriod, second: ChartPeriod): ChartPeriod => {
  if (first === CHART_PERIOD.UNSET || second === CHART_PERIOD.UNSET) {
    return CHART_PERIOD.UNSET;
  }

  if (first === CHART_PERIOD.MONTH || second === CHART_PERIOD.MONTH) {
    return CHART_PERIOD.MONTH;
  }

  if (first === CHART_PERIOD.WEEK || second === CHART_PERIOD.WEEK) {
    return CHART_PERIOD.WEEK;
  }

  return CHART_PERIOD.DAY;
};

export enum CHART_PERIOD {
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  UNSET = "UNSET",
}
export type ChartPeriod = CHART_PERIOD.DAY | CHART_PERIOD.WEEK | CHART_PERIOD.MONTH | CHART_PERIOD.UNSET;

export const useChartPeriodMeasure = (dates: Date[], horizontalMarginWidth = 0, isBar = false) => {
  const [dimensions, ref] = useDimensions();

  const ticks = useMemo<string[]>(() => {
    const upperRange = dimensions ? dimensions.width - horizontalMarginWidth : 0;
    return timeScale().domain(dates).range([0, upperRange]).ticks(getTickCountBySize(upperRange));
  }, [dates, dimensions, horizontalMarginWidth]);

  /**
   * Use d3 time scale to test the difference between dates
   */
  const diff = useMemo<ChartPeriod>(() => {
    if (!dimensionsValid(dimensions)) {
      return CHART_PERIOD.UNSET;
    }

    let first: moment.Moment, second: moment.Moment;

    /**
     * The first tick will always be the start of the user's selected
     * date range, while the second will be the next "logical" one
     * for example, the start of the next month. This means that
     * the difference between the first two ticks may not be indicative
     * of the period of the chart, so we use the third tick if possible
     * to get a more accurate period.
     */
    if (ticks.length > 2) {
      first = moment(ticks[1]);
      second = moment(ticks[2]);
    } else {
      first = moment(ticks[0]);
      second = moment(ticks[1]);
    }

    let computedPeriod: ChartPeriod;

    if (Math.floor(second.diff(first, "weeks")) > 2) {
      computedPeriod = CHART_PERIOD.MONTH;
    } else if (Math.floor(second.diff(first, "days")) > 2) {
      computedPeriod = CHART_PERIOD.WEEK;
    } else {
      computedPeriod = CHART_PERIOD.DAY;
    }

    if (isBar) {
      return maxChartPeriod(barChartSizing(dimensions!.width - horizontalMarginWidth), computedPeriod);
    }

    return computedPeriod;
  }, [dimensions, horizontalMarginWidth, isBar, ticks]);

  return [diff, ref] as const;
};
